/**
 * Meta-driven autofill engine for dev mode
 * 
 * Fills empty fields on any doctype detail page using ERPNext meta schema.
 * Completely generic - no doctype-specific hardcoding.
 */

import type { DocTypeSchema, DocFieldSchema } from "@/lib/erp/meta";

/**
 * Field types that should be skipped (non-input, layout, attachments)
 */
const SKIP_FIELDTYPES = new Set([
  "Section Break",
  "Column Break",
  "Tab Break",
  "HTML",
  "Button",
  "Attach",
  "Attach Image",
  "Heading",
  "Fold",
  "Read Only",
  "Barcode",
  "Geolocation",
  "Color",
  "Duration",
  "Time",
  "Rating",
]);

/**
 * Field names that should be skipped (system-managed)
 */
const SKIP_FIELDNAMES = new Set([
  "name",
  "owner",
  "creation",
  "modified",
  "modified_by",
  "docstatus",
  "idx",
  "parent",
  "parenttype",
  "parentfield",
]);

/**
 * Check if a fieldname should be skipped (system fields)
 */
function shouldSkipFieldname(fieldname: string): boolean {
  // Skip system fields
  if (SKIP_FIELDNAMES.has(fieldname)) {
    return true;
  }
  // Skip fields starting with __
  if (fieldname.startsWith("__")) {
    return true;
  }
  return false;
}

/**
 * Check if a value is "empty" (should be filled)
 */
function isEmpty(value: any): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  // For numbers, 0 is NOT empty (user might have set it intentionally)
  // For booleans, false is NOT empty
  return false;
}

/**
 * Generate a unique string value for a field
 */
function generateUniqueValue(fieldname: string): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 6);
  return `AUTO-${fieldname}-${timestamp}-${random}`;
}

/**
 * Generate a random value for a Data/Text field
 */
function generateTextValue(fieldname: string): string {
  return generateUniqueValue(fieldname);
}

/**
 * Generate a random number value
 */
function generateNumberValue(fieldtype: string): number {
  if (fieldtype === "Int") {
    return Math.floor(Math.random() * 1000) + 1;
  }
  if (fieldtype === "Float" || fieldtype === "Currency" || fieldtype === "Percent") {
    return Math.round((Math.random() * 1000 + 1) * 100) / 100;
  }
  return Math.floor(Math.random() * 1000) + 1;
}

/**
 * Generate a random date value
 */
function generateDateValue(): string {
  const today = new Date();
  const daysAgo = Math.floor(Math.random() * 365);
  const date = new Date(today);
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split("T")[0];
}

/**
 * Pick a random option from Select field
 */
function pickRandomSelectOption(field: DocFieldSchema): string | null {
  if (!field.options) {
    return null;
  }
  const options = field.options.split("\n").filter(Boolean);
  if (options.length === 0) {
    return null;
  }
  const randomIndex = Math.floor(Math.random() * options.length);
  return options[randomIndex];
}

/**
 * Check if target doctype has disabled/enabled field
 */
async function getLinkFilters(
  targetDoctype: string,
  ensureMeta: (doctype: string) => Promise<DocTypeSchema>
): Promise<Record<string, any> | undefined> {
  try {
    const targetMeta = await ensureMeta(targetDoctype);
    const hasDisabled = targetMeta.fields.some((f) => f.fieldname === "disabled");
    const hasEnabled = targetMeta.fields.some((f) => f.fieldname === "enabled");

    if (hasDisabled) {
      return { disabled: 0 };
    }
    if (hasEnabled) {
      return { enabled: 1 };
    }
    return undefined;
  } catch (error) {
    // If meta fetch fails, skip filters (safer than guessing)
    if (import.meta.env.DEV) {
      console.warn(`[autofill] Failed to fetch meta for ${targetDoctype}, skipping filters:`, error);
    }
    return undefined;
  }
}


/**
 * Generate a child table row matching useChildTable conventions
 */
function generateChildTableRow(
  childFields: DocFieldSchema[],
  currentRowCount: number
): Record<string, any> {
  const row: Record<string, any> = {
    idx: currentRowCount + 1,
    docstatus: 0,
    __islocal: 1,
  };

  // Apply defaults from child meta
  for (const field of childFields) {
    if (field.default !== undefined) {
      row[field.fieldname] = field.default;
    }
  }

  return row;
}

/**
 * Fill a single child table row's fields (recursive autofill)
 */
async function fillChildTableRow(
  row: Record<string, any>,
  childFields: DocFieldSchema[],
  ensureMeta: (doctype: string) => Promise<DocTypeSchema>,
  searchLink: (doctype: string, filters: Record<string, any> | undefined) => Promise<string | null>
): Promise<Record<string, any>> {
  const filledRow = { ...row };

  for (const field of childFields) {
    // Skip system fields and hidden fields
    if (shouldSkipFieldname(field.fieldname) || field.hidden === 1 || field.read_only === 1) {
      continue;
    }

    // Skip if already has value
    if (!isEmpty(filledRow[field.fieldname])) {
      continue;
    }

    // Skip non-input fieldtypes
    if (SKIP_FIELDTYPES.has(field.fieldtype)) {
      continue;
    }

    // Fill based on fieldtype
    switch (field.fieldtype) {
      case "Data":
      case "Small Text":
      case "Text":
        filledRow[field.fieldname] = generateTextValue(field.fieldname);
        break;

      case "Long Text":
      case "Text Editor":
        filledRow[field.fieldname] = generateTextValue(field.fieldname);
        break;

      case "Int":
      case "Float":
      case "Currency":
      case "Percent":
        filledRow[field.fieldname] = generateNumberValue(field.fieldtype);
        break;

      case "Date":
        filledRow[field.fieldname] = generateDateValue();
        break;

      case "Datetime":
        // Use current datetime
        filledRow[field.fieldname] = new Date().toISOString().slice(0, 19).replace("T", " ");
        break;

      case "Check":
        filledRow[field.fieldname] = 1;
        break;

      case "Select":
        const selectValue = pickRandomSelectOption(field);
        if (selectValue) {
          filledRow[field.fieldname] = selectValue;
        }
        break;

      case "Link":
        if (field.options) {
          const filters = await getLinkFilters(field.options, ensureMeta);
          const linkValue = await searchLink(field.options, filters);
          if (linkValue) {
            filledRow[field.fieldname] = linkValue;
          }
        }
        break;

      // Skip Table fields inside child tables (nested tables not supported)
      case "Table":
        // Skip nested tables
        break;

      default:
        // For unknown types, try text value
        filledRow[field.fieldname] = generateTextValue(field.fieldname);
        break;
    }
  }

  return filledRow;
}

/**
 * Options for autofill operation
 */
export interface AutoFillOptions {
  /** Only fill empty fields (default: true) */
  fillEmptyOnly?: boolean;
  /** Default number of rows for table fields (default: 5) */
  defaultChildRows?: number;
  /** Override for number of rows (if user selected specific N) */
  rowsOverride?: number;
  /** Max concurrent link searches (default: 4) */
  linkConcurrency?: number;
}

/**
 * Autofill a detail page form using meta-driven logic
 * 
 * @param doctype - DocType name
 * @param metaSchema - Meta schema for the doctype
 * @param getFormData - Function to get current form data
 * @param ensureMeta - Function to ensure meta is fetched (for child tables and link targets)
 * @param searchLink - Function to search link options
 * @param options - Autofill options
 * @returns Promise resolving to patches to apply (field patches + table patches)
 */
export async function autoFill(
  _doctype: string,
  metaSchema: DocTypeSchema,
  getFormData: () => Record<string, any>,
  ensureMeta: (doctype: string) => Promise<DocTypeSchema>,
  searchLink: (
    targetDoctype: string,
    filters: Record<string, any> | undefined
  ) => Promise<string | null>,
  options: AutoFillOptions = {}
): Promise<{
  fieldPatch: Record<string, any>;
  tablePatches: Array<{ fieldname: string; rows: any[] }>;
}> {
  const {
    fillEmptyOnly = true,
    defaultChildRows = 5,
    rowsOverride,
    linkConcurrency = 4,
  } = options;

  const formData = getFormData();
  const fieldPatch: Record<string, any> = {};
  const tablePatches: Array<{ fieldname: string; rows: any[] }> = [];

  // Track link searches for concurrency limiting
  const linkSearchTasks: Array<{
    fieldname: string;
    promise: Promise<string | null>;
  }> = [];

  // Process each field
  for (const field of metaSchema.fields) {
    // Skip system fields and hidden fields
    if (shouldSkipFieldname(field.fieldname) || field.hidden === 1 || field.read_only === 1) {
      continue;
    }

    // Skip non-input fieldtypes
    if (SKIP_FIELDTYPES.has(field.fieldtype)) {
      continue;
    }

    const currentValue = formData[field.fieldname];

    // Check if should fill (empty check)
    if (fillEmptyOnly && !isEmpty(currentValue)) {
      continue;
    }

    // Fill based on fieldtype
    switch (field.fieldtype) {
      case "Data":
      case "Small Text":
      case "Text":
        fieldPatch[field.fieldname] = generateTextValue(field.fieldname);
        break;

      case "Long Text":
      case "Text Editor":
        fieldPatch[field.fieldname] = generateTextValue(field.fieldname);
        break;

      case "Int":
      case "Float":
      case "Currency":
      case "Percent":
        fieldPatch[field.fieldname] = generateNumberValue(field.fieldtype);
        break;

      case "Date":
        fieldPatch[field.fieldname] = generateDateValue();
        break;

      case "Datetime":
        fieldPatch[field.fieldname] = new Date().toISOString().slice(0, 19).replace("T", " ");
        break;

      case "Check":
        fieldPatch[field.fieldname] = 1;
        break;

      case "Select":
        const selectValue = pickRandomSelectOption(field);
        if (selectValue) {
          fieldPatch[field.fieldname] = selectValue;
        }
        break;

      case "Link":
        if (field.options) {
          // Queue link search (will be executed with concurrency limit)
          const linkPromise = (async () => {
            const filters = await getLinkFilters(field.options!, ensureMeta);
            return await searchLink(field.options!, filters);
          })();
          linkSearchTasks.push({
            fieldname: field.fieldname,
            promise: linkPromise,
          });
        }
        break;

      case "Table":
        if (field.options) {
          // Fetch child meta
          try {
            const childMeta = await ensureMeta(field.options);
            
            // Get visible fields (same logic as ChildTableField)
            const visibleFields = childMeta.fields.filter(
              (f) =>
                f.hidden !== 1 &&
                !f.fieldname.startsWith("__") &&
                !["idx", "docstatus", "__islocal", "name", "parent", "parenttype", "parentfield"].includes(
                  f.fieldname
                )
            );

            const numRows = rowsOverride ?? defaultChildRows;
            const currentRows = Array.isArray(currentValue) ? currentValue : [];
            
            // Generate N new rows
            const newRows: any[] = [];
            for (let i = 0; i < numRows; i++) {
              const baseRow = generateChildTableRow(visibleFields, currentRows.length + i);
              
              // Fill row fields (recursive autofill)
              const filledRow = await fillChildTableRow(
                baseRow,
                visibleFields,
                ensureMeta,
                searchLink
              );
              
              newRows.push(filledRow);
            }

            // Append to existing rows (or create new array)
            const allRows = [...currentRows, ...newRows];
            tablePatches.push({ fieldname: field.fieldname, rows: allRows });
          } catch (error) {
            if (import.meta.env.DEV) {
              console.warn(`[autofill] Failed to fill table field ${field.fieldname}:`, error);
            }
          }
        }
        break;

      default:
        // For unknown types, try text value
        fieldPatch[field.fieldname] = generateTextValue(field.fieldname);
        break;
    }
  }

  // Wait for all link searches to complete (with concurrency limit)
  // Process in batches of linkConcurrency
  const batches: Array<typeof linkSearchTasks> = [];
  for (let i = 0; i < linkSearchTasks.length; i += linkConcurrency) {
    batches.push(linkSearchTasks.slice(i, i + linkConcurrency));
  }

  for (const batch of batches) {
    const results = await Promise.all(batch.map((task) => task.promise));
    // Apply results to fieldPatch
    batch.forEach((task, index) => {
      const linkValue = results[index];
      if (linkValue) {
        fieldPatch[task.fieldname] = linkValue;
      }
    });
  }

  return {
    fieldPatch,
    tablePatches,
  };
}
